import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertTriangle, FileText, ArrowRight, RefreshCw } from 'lucide-react';
import { useAuthStore } from '../stores/authStore';
import { RoleRoutes } from '@/types/global.types';

interface AccountRejectedModalProps {
  isOpen: boolean;
  onConfirm: () => void;
  rejectionReason?: string;
  rejectionFields?: string[];
}

// Map of field names to French labels
const FIELD_LABELS: Record<string, string> = {
  cnie: 'Numéro CIN',
  passport: 'Numéro de passeport',
  nomArabe: 'Nom en arabe',
  prenomArabe: 'Prénom en arabe',
  sexe: 'Sexe',
  dateNaissance: 'Date de naissance',
  paysOrigine: 'Pays d\'origine',
  handicap: 'Situation de handicap',
  adresse: 'Adresse',
  ville: 'Ville',
  codePostal: 'Code postal',
  urgenceNom: 'Nom du contact d\'urgence',
  urgenceNumero: 'Numéro du contact d\'urgence',
  urgenceAdresse: 'Adresse du contact d\'urgence',
  urgenceRelation: 'Relation avec le contact d\'urgence',
  phoneCountry: 'Indicatif téléphonique',
  rib: 'RIB',
  salaireMensuelNet: 'Salaire mensuel net',
  fonction: 'Fonction',
  diplomeId: 'Diplôme',
  dateRecrutement: 'Date de recrutement',
  statutProfesseur: 'Statut professeur',
  departementAttacheId: 'Département',
  estOriginaireEtablissementPublic: 'Origine établissement public',
  etablissementOrigine: 'Établissement d\'origine',
  gradeId: 'Grade',
  specialite: 'Spécialité',
  matieresEnseignees: 'Matières enseignées',
  tauxHoraire: 'Taux horaire',
  relationAvecEtudiant: 'Relation avec l\'étudiant',
  relationPrecision: 'Précision de la relation',
  documents: 'Documents',
};

/**
 * Modal displayed when a user's account has been rejected.
 * Shows rejection reason and fields that need correction.
 */
export const AccountRejectedModal: React.FC<AccountRejectedModalProps> = ({
  isOpen,
  onConfirm,
  rejectionReason,
  rejectionFields = [],
}) => {
  const navigate = useNavigate();
  const { user } = useAuthStore();

  if (!isOpen) return null;

  // Build profile URL based on user role
  const getProfileUrl = () => {
    if (!user) return '/profile';
    const roleRoute = RoleRoutes[user.role];
    if (!roleRoute) return '/profile';
    return roleRoute.replace(':tenantId', user.tenantId || '') + '/profile';
  };

  const handleGoToProfile = () => {
    onConfirm();
    navigate(getProfileUrl());
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="relative w-full max-w-lg bg-white rounded-2xl shadow-2xl overflow-hidden animate-in zoom-in-95 duration-300">
        {/* Header */}
        <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-6 text-white relative overflow-hidden">
          {/* Decorative elements */}
          <div className="absolute -right-6 -top-6 w-24 h-24 bg-white/10 rounded-full" />
          <div className="absolute -right-2 top-10 w-12 h-12 bg-white/10 rounded-full" />

          <div className="relative flex items-start gap-4">
            <div className="flex-shrink-0 w-12 h-12 bg-white/20 rounded-full flex items-center justify-center">
              <AlertTriangle className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold mb-1">Profil rejeté</h2>
              <p className="text-red-100 text-sm">
                Des modifications sont nécessaires avant validation
              </p>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {/* Rejection reason */}
          {rejectionReason && (
            <div className="mb-5 p-4 bg-red-50 border border-red-200 rounded-xl">
              <p className="text-sm font-medium text-red-800 mb-1">Motif du rejet :</p>
              <p className="text-sm text-red-700">{rejectionReason}</p>
            </div>
          )}

          {/* Fields to correct */}
          {rejectionFields.length > 0 && (
            <div className="mb-5">
              <p className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                <FileText className="w-4 h-4 text-gray-500" />
                Informations à corriger :
              </p>
              <div className="flex flex-wrap gap-2">
                {rejectionFields.map((field) => (
                  <span
                    key={field}
                    className="px-3 py-1.5 bg-amber-100 text-amber-800 rounded-full text-sm font-medium"
                  >
                    {FIELD_LABELS[field] || field}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Instructions */}
          <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
            <div className="flex gap-3">
              <RefreshCw className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm text-blue-800 font-medium">Comment procéder ?</p>
                <ol className="text-sm text-blue-700 mt-2 space-y-1 list-decimal list-inside">
                  <li>Accédez à votre page de profil</li>
                  <li>Corrigez les informations demandées</li>
                  <li>Soumettez à nouveau votre profil</li>
                </ol>
                <p className="text-sm text-blue-700 mt-2">
                  Après re-soumission, vous retrouverez un accès temporaire à la plateforme.
                </p>
              </div>
            </div>
          </div>

          {/* Action button */}
          <button
            onClick={handleGoToProfile}
            className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white font-semibold rounded-xl transition-colors flex items-center justify-center gap-2 shadow-lg shadow-primary-500/25"
          >
            Corriger mon profil
            <ArrowRight className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default AccountRejectedModal;
